import { useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import type { BlockKind, EditorNode, InsertSlot } from './editor-types';
import type { Pointer } from './json-pointer';

/**
 * Arbre des blocs (calques, canevas) : sélection, ajout par la palette de la liste, glisser-déposer
 * (dnd-kit, ADR-101) vers une autre place d'une liste du même groupe, monter et descendre au bouton,
 * retrait confirmé. Générique : il ne connaît que des nœuds, des listes et des groupes.
 */

export type MoveTarget = { list: Pointer; index: number };
type DragData = { list: Pointer; index: number; group: string; label: string };

/** Place d'arrivée d'un dépôt : la position du bloc survolé, dans sa liste, si le groupe concorde. */
export function dropTarget(active: DragData | undefined, over: DragData | undefined): MoveTarget | null {
  if (active === undefined || over === undefined || active.group !== over.group) return null;
  if (active.list === over.list && active.index === over.index) return null;
  return { list: over.list, index: over.index };
}

export function LayerTree<D>({
  root,
  selected,
  onSelect,
  onInsert,
  onMove,
  onRemove,
  counts,
}: {
  root: EditorNode<D>;
  selected: Pointer;
  onSelect: (pointer: Pointer) => void;
  onInsert: (slot: InsertSlot<D>, kind: BlockKind<D>) => void;
  onMove: (node: EditorNode<D>, target: MoveTarget) => void;
  onRemove: (node: EditorNode<D>) => void;
  counts: ReadonlyMap<Pointer, { errors: number; warnings: number }>;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));
  const byPointer = new Map<Pointer, EditorNode<D>>();
  const index = (node: EditorNode<D>): void => {
    byPointer.set(node.pointer, node);
    node.children.forEach(index);
  };
  index(root);
  const onDragEnd = (event: DragEndEvent): void => {
    const target = dropTarget(event.active.data.current as DragData | undefined, event.over?.data.current as DragData | undefined);
    const node = byPointer.get(String(event.active.id));
    if (target !== null && node !== undefined) onMove(node, target);
  };
  return (
    <DndContext
      sensors={sensors}
      onDragEnd={onDragEnd}
      accessibility={{
        screenReaderInstructions: { draggable: 'Pour déplacer un bloc : Espace pour le saisir, flèches pour choisir sa place, Espace pour le déposer, Échap pour annuler.' },
        announcements: {
          onDragStart: ({ active }) => `Bloc ${(active.data.current as DragData | undefined)?.label ?? ''} saisi.`,
          onDragOver: ({ over }) => (over === null ? 'Hors des blocs.' : `Au-dessus de ${(over.data.current as DragData | undefined)?.label ?? ''}.`),
          onDragEnd: ({ over }) => (over === null ? 'Bloc reposé à sa place.' : `Bloc déposé à la place de ${(over.data.current as DragData | undefined)?.label ?? ''}.`),
          onDragCancel: () => 'Déplacement annulé.',
        },
      }}
    >
      <nav className="ay-editor__tree" aria-label="Blocs du document">
        <ul role="tree" className="ay-editor__tree-list">
          <TreeItem node={root} depth={0} selected={selected} onSelect={onSelect} onInsert={onInsert} onMove={onMove} onRemove={onRemove} counts={counts} />
        </ul>
      </nav>
    </DndContext>
  );
}

function TreeItem<D>({ node, depth, selected, onSelect, onInsert, onMove, onRemove, counts }: {
  node: EditorNode<D>;
  depth: number;
  selected: Pointer;
  onSelect: (pointer: Pointer) => void;
  onInsert: (slot: InsertSlot<D>, kind: BlockKind<D>) => void;
  onMove: (node: EditorNode<D>, target: MoveTarget) => void;
  onRemove: (node: EditorNode<D>) => void;
  counts: ReadonlyMap<Pointer, { errors: number; warnings: number }>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [palette, setPalette] = useState<number | null>(null);
  const position = node.position;
  const data: DragData | undefined = position === undefined ? undefined : { list: position.list, index: position.index, group: position.group, label: node.label };
  const drag = useDraggable({ id: node.pointer, disabled: position === undefined, ...(data === undefined ? {} : { data }) });
  const drop = useDroppable({ id: `drop:${node.pointer}`, disabled: position === undefined, ...(data === undefined ? {} : { data }) });
  const isSelected = node.pointer === selected;
  const count = counts.get(node.pointer);
  return (
    <li role="treeitem" aria-selected={isSelected} aria-level={depth + 1} className="ay-editor__node">
      <div ref={drop.setNodeRef} className={`ay-editor__node-row${isSelected ? ' is-selected' : ''}${drop.isOver ? ' is-over' : ''}${drag.isDragging ? ' is-dragging' : ''}`} style={{ paddingInlineStart: `${8 + depth * 14}px` }}>
        {position !== undefined && (
          <button type="button" ref={drag.setNodeRef} className="ay-editor__handle" aria-label={`Déplacer ${node.label}`} {...drag.attributes} {...drag.listeners}>⠿</button>
        )}
        <button type="button" className="ay-editor__node-main" onClick={() => onSelect(node.pointer)} aria-current={isSelected ? 'true' : undefined}>
          {node.glyph !== undefined && <span className="ay-editor__glyph" aria-hidden="true">{node.glyph}</span>}
          <span className="ay-editor__node-label">{node.label}</span>
          {node.detail !== undefined && <span className="ay-editor__node-detail">{node.detail}</span>}
          {/* Deux pastilles au plus dans l'arbre ; le panneau de propriétés dit le reste. */}
          {(node.marks ?? []).slice(0, 2).map((mark) => (
            <span key={mark} className="ay-editor__mark">{mark}</span>
          ))}
          {count !== undefined && count.errors > 0 && <span className="ay-editor__count ay-editor__count--error" title="Erreurs">{count.errors}</span>}
          {count !== undefined && count.errors === 0 && count.warnings > 0 && <span className="ay-editor__count ay-editor__count--warning" title="Avertissements">{count.warnings}</span>}
        </button>
        {position !== undefined && (
          <span className="ay-editor__node-tools">
            <button type="button" className="ay-editor__tool" aria-label={`Monter ${node.label}`} disabled={position.index === 0} onClick={() => onMove(node, { list: position.list, index: position.index - 1 })}>↑</button>
            <button type="button" className="ay-editor__tool" aria-label={`Descendre ${node.label}`} disabled={position.index >= position.size - 1} onClick={() => onMove(node, { list: position.list, index: position.index + 1 })}>↓</button>
            {confirming ? (
              <span className="ay-editor__confirm" role="group" aria-label={`Retirer ${node.label} ?`}>
                <button type="button" className="ay-editor__tool ay-editor__tool--danger" onClick={() => { setConfirming(false); onRemove(node); }}>Retirer</button>
                <button type="button" className="ay-editor__tool" onClick={() => setConfirming(false)}>Garder</button>
              </span>
            ) : (
              <button type="button" className="ay-editor__tool" aria-label={`Retirer ${node.label}`} disabled={position.size <= (position.minimum ?? 0)} onClick={() => setConfirming(true)}>×</button>
            )}
          </span>
        )}
      </div>
      {(node.children.length > 0 || (node.insert ?? []).length > 0) && (
        <ul role="group" className="ay-editor__tree-list">
          {node.children.map((child) => (
            <TreeItem key={child.pointer} node={child} depth={depth + 1} selected={selected} onSelect={onSelect} onInsert={onInsert} onMove={onMove} onRemove={onRemove} counts={counts} />
          ))}
          {(node.insert ?? []).map((slot, index) => (
            <li key={slot.list} className="ay-editor__insert" style={{ paddingInlineStart: `${8 + (depth + 1) * 14}px` }}>
              {palette === index ? (
                <span className="ay-editor__palette" role="group" aria-label={slot.label}>
                  {slot.kinds.map((kind) => (
                    <button key={kind.id} type="button" className="ay-editor__palette-item" onClick={() => { setPalette(null); onInsert(slot, kind); }}>
                      {kind.glyph !== undefined && <span className="ay-editor__glyph" aria-hidden="true">{kind.glyph}</span>}
                      {kind.label}
                    </button>
                  ))}
                  <button type="button" className="ay-editor__tool" onClick={() => setPalette(null)} aria-label="Fermer la palette">×</button>
                </span>
              ) : (
                <button type="button" className="ay-editor__add" onClick={() => setPalette(index)}>+ {slot.label}</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
